import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import { FaDownload, FaPlus, FaEdit, FaTrash, FaEye, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ReceiptFormModal from './ReceiptFormModal';
import ReceiptViewModal from './ReceiptViewModal';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useScreenPermissions } from '../components/useScreenPermissions';

const API_URL = '/api';

const ReceiptFromProductionItems = () => {
  const [searchTerm, setSearchTerm] = useState('');
   const navigate = useNavigate();
  const [status, setStatus] = useState('All');
  const [inventoryFilter, setInventoryFilter] = useState('All');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [stats, setStats] = useState({
    totalReceipts: 0,
    withInventoryFlag: 0,
    withoutInventoryFlag: 0,
    createdThisMonth: 0
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  
  // Add user state
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);

  // --- THEME HOOKS (must run unconditionally) ---
  // Use slate-800 (#1f2937) as accent so Create New, active sidebar, logout, and profile match.
  const pageTheme = useMemo(() => ({
    bg: '#f3f4f6',
    sidebarBg: '#ffffff',
    accent: '#1f2937', // slate-800 (Create New button & active sidebar)
    border: '#d1d5db',
    text: '#0f1724',
    muted: '#6b7280',
    iconColor: '#1f2937',
    hoverBg: '#eff6ff',
    logout: '#1f2937' // slate-800 for logout/profile requested
  }), []);

  // Inject CSS variables and scoped stylesheet so Sidebar matches pageTheme
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accent,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--sidebar-active-text': pageTheme.accent,
      '--sidebar-profile-bg': pageTheme.logout,
      '--logout-bg': pageTheme.logout
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'receipt-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Sidebar base: white background, black text for non-active items */
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item,
      .sidebar .nav-link, .app-sidebar .nav-link { color: var(--sidebar-text) !important; background-color: #ffffff !important; }

      /* Active item: use accent (slate-800) background with white text & icons */
      .sidebar a.active, .sidebar .nav-link.active, .sidebar .menu-item.active, .sidebar .nav-item.active,
      .app-sidebar a.active, .app-sidebar .nav-link.active {
        background-color: var(--accent-color) !important; /* slate-800 background */
        color: #ffffff !important; /* active text white and visible */
        font-weight: 700;
      }

      /* Force all nested text inside the active item to be white (covers many markup variants) */
      .sidebar a.active *,
      .sidebar .menu-item.active *,
      .sidebar .nav-link.active *,
      .app-sidebar a.active *,
      .app-sidebar .menu-item.active *,
      .app-sidebar .nav-link.active * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Specific common label selectors some sidebars use */
      .sidebar a.active .menu-text,
      .sidebar a.active .menu-item-text,
      .sidebar a.active .item-label,
      .sidebar .menu-item.active .menu-text,
      .sidebar .menu-item.active .menu-item-text,
      .sidebar .menu-item.active .item-label {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      /* Ensure active icons also use white so they are visible on slate background */
      .sidebar a.active svg, .sidebar .nav-link.active svg, .app-sidebar a.active svg {
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* If sidebar implementation used an inset bar, keep it subtle or remove */
      .sidebar a.active::before, .app-sidebar a.active::before {
        background: transparent !important;
      }

      /* Catch-all: if sidebar uses Tailwind classes or inline slate colors, force white text for those elements */
      .sidebar .bg-slate-800, .sidebar .bg-slate-700,
      .app-sidebar .bg-slate-800, .app-sidebar .bg-slate-700,
      .sidebar [class*="bg-slate-800"], .sidebar [class*="bg-slate-700"],
      .app-sidebar [class*="bg-slate-800"], .app-sidebar [class*="bg-slate-700"],
      .sidebar [style*="#1f2937"], .sidebar [style*="#374151"],
      .app-sidebar [style*="#1f2937"], .app-sidebar [style*="#374151"],
      .sidebar [style*="background-color: var(--accent-color)"], .app-sidebar [style*="background-color: var(--accent-color)"] {
        color: #ffffff !important;
      }

      .sidebar .bg-slate-800 *, .sidebar .bg-slate-700 *,
      .app-sidebar .bg-slate-800 *, .app-sidebar .bg-slate-700 *,
      .sidebar [class*="bg-slate-800"] *, .sidebar [class*="bg-slate-700"] *,
      .app-sidebar [class*="bg-slate-800"] *, .app-sidebar [class*="bg-slate-700"] *,
      .sidebar [style*="#1f2937"] *, .sidebar [style*="#374151"] *,
      .app-sidebar [style*="#1f2937"] *, .app-sidebar [style*="#374151"] *,
      .sidebar [style*="background-color: var(--accent-color)"] *, .app-sidebar [style*="background-color: var(--accent-color)"] * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Profile avatar / icon in sidebar: slate-800 background with white icon/text */
      .sidebar .profile .avatar, .sidebar .profile .icon, .sidebar .profile svg,
      .app-sidebar .profile .avatar, .app-sidebar .profile .icon, .app-sidebar .profile svg {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
      }

      /* Logout button style: slate-800 bg with white text */
      .sidebar .logout-button, .app-sidebar .logout-button,
      .sidebar .profile-action, .sidebar .logout, .app-sidebar .logout,
      .sidebar .logout-btn, .app-sidebar .logout-btn {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        border-radius: 4px !important;
        padding: 6px 10px !important;
      }

      /* Ensure non-active sidebar items remain white with dark text (explicitly override other styles) */
      .sidebar .menu-item, .app-sidebar .menu-item, .sidebar .nav-link, .app-sidebar .nav-link {
        background-color: #ffffff !important;
        color: var(--sidebar-text) !important;
      }

      /* Make sure hover still looks okay */
      .sidebar a:hover, .app-sidebar a:hover { background-color: rgba(0,0,0,0.03) !important; }
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);
  // --- end theme hooks ---

  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No authentication token found. Please log in again.');
        return false;
      }
      
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (userRes.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('authToken');
        setIsUserValid(false);
        setAuthError('Your session has expired. Please log in again.');
        return false;
      }
      
      if (!userRes.ok) throw new Error('Failed to fetch user');
      
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        return true;
      } else {
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page and log in again.');
      return false;
    }
  };

  // Fetch receipts
  useEffect(() => {
    const initializeApp = async () => {
      const userValid = await fetchCurrentUser();
      if (!userValid) {
        setLoading(false);
        return;
      }
      fetchReceipts();
    };
    initializeApp();
  }, []);

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      const response = await fetch(`${API_URL}/receipt-from-production`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch receipts');
      const data = await response.json();
      setReceipts(data);
      calculateStats(data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      alert('Error loading receipts');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (receiptData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    let totalReceipts = receiptData.length;
    let withInventoryFlag = 0;
    let withoutInventoryFlag = 0;
    let createdThisMonth = 0;
    
    receiptData.forEach(receipt => {
      if (receipt.InventoryFlag === true || receipt.InventoryFlag === 1) {
        withInventoryFlag++;
      } else {
        withoutInventoryFlag++;
      }
      const receiptDate = new Date(receipt.ReceiptDate);
      if (receiptDate.getMonth() === currentMonth && receiptDate.getFullYear() === currentYear) {
        createdThisMonth++;
      }
    });
    
   setStats({
      totalReceipts,
      withInventoryFlag,
      withoutInventoryFlag,
      createdThisMonth
    });
  };

   const handleLogout = () => {
    navigate('/login');
  };

  const handleCreate = () => {
    if (!isUserValid) {
      alert(authError || 'You must be logged in to create receipts');
      return;
    }
    setSelectedReceipt(null);
    setShowModal(true);
  };

  const handleEdit = (receipt) => {
    if (!isUserValid) {
      alert(authError || 'You must be logged in to edit receipts');
      return;
    }
    
    setSelectedReceipt(receipt);
    setShowModal(true);
  };

  const handleView = (receipt) => {
    if (!isUserValid) {
      alert(authError || 'You must be logged in to view receipts');
      return;
    }
    
    setSelectedReceipt(receipt);
    setViewModalOpen(true);
  };

  const handleDelete = async (receiptId) => {
    if (!isUserValid) {
      alert(authError || 'You must be logged in to delete receipts');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete Receipt ${receiptId}?`)) {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          window.location.href = '/login';
          return;
        }
        
        const response = await fetch(`${API_URL}/receipt-from-production/${receiptId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
          return;
        }
        
        if (!response.ok) throw new Error('Failed to delete receipt');
        
        // Refresh the list
        fetchReceipts();
        alert('Receipt deleted successfully');
      } catch (error) {
        console.error('Error deleting receipt:', error);
        alert('Error deleting receipt');
      }
    }
  };

  const handleModalSubmit = () => {
    // Refresh the list after form submission
    fetchReceipts();
  };

  // Sorting
   const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue, bValue;
      switch(sortConfig.key) {
        case 'ReceiptID':
        case 'ReceivedByUserName':
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
          break;
        case 'ReceiptDate':
          aValue = new Date(a[sortConfig.key]);
          bValue = new Date(b[sortConfig.key]);
          break;
        case 'ProductionOrderQty':
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
          break;
        case 'Status':
          case 'InventoryFlag':
          aValue = a[sortConfig.key] ? 1 : 0;
          bValue = b[sortConfig.key] ? 1 : 0;
          break;
          // In the getSortedData function, update the status handling:
case 'Status':
          aValue = a[sortConfig.key] || 'INACTIVE';
          bValue = b[sortConfig.key] || 'INACTIVE';
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredReceipts = receipts.filter(receipt => {
    const matchesSearch = receipt.ReceiptID.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (receipt.ReceivedByUserName && receipt.ReceivedByUserName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (receipt.ProductionOrderID && receipt.ProductionOrderID.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = status === 'All' || (status === 'Active' && receipt.Status === 'ACTIVE') || (status === 'Inactive' && receipt.Status === 'INACTIVE');
    const hasInventoryFlag = receipt.InventoryFlag === 1 || receipt.InventoryFlag === true;
    const matchesInventoryFlag = inventoryFilter === 'All' || (inventoryFilter === 'Yes' && hasInventoryFlag) || (inventoryFilter === 'No' && !hasInventoryFlag);
    return matchesSearch && matchesStatus && matchesInventoryFlag;
  });

  const sortedReceipts = getSortedData(filteredReceipts);
  const totalPages = Math.ceil(sortedReceipts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedReceipts.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';
    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${isAsc ? 'text-blue-600' : 'text-gray-500'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${isDesc ? 'text-blue-600' : 'text-gray-500'}`} />
      </span>
    );
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    pages.push(
      <button key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`p-2 transition-all duration-200 ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}>
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => handlePageChange(i)} className={`px-4 py-1 font-medium transition-all duration-200 ${i === currentPage ? 'bg-slate-800 text-white' : 'text-blue-600 hover:bg-slate-700'}`}>
          {i}
        </button>
      );
    }
    pages.push(
      <button key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`p-2 transition-all duration-200 ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}>
        <FaChevronRight className="h-5 w-5" />
      </button>
    );
    return pages;
  };

  const canCreate = hasFullAccess || hasPermission('RECEIPT_PRODUCTION', 'CanCreate');
  const canView = hasFullAccess || hasPermission('RECEIPT_PRODUCTION', 'CanView');
  const canEdit = hasFullAccess || hasPermission('RECEIPT_PRODUCTION', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('RECEIPT_PRODUCTION', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-700">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!isUserValid && !permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
            <p className="font-bold">Authentication Error</p>
            <p>{authError || 'You must be logged in to access this page.'}</p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => window.location.href = '/login'}>Go to Login Page</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: pageTheme.bg, '--page-border-color': pageTheme.border, '--page-background': pageTheme.bg }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }} 
      onLogout={handleLogout}/>
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b" style={{ borderColor: pageTheme.border, paddingBottom: 16 }}>
          <div className="flex-1">
            <h1 className="text-3xl font-bold" style={{ color: pageTheme.text }}>Receipt from Production</h1>
            <p className="mt-1 text-base" style={{ color: pageTheme.muted }}>Manage receipts from production orders here.</p>
          </div>
          <div className="flex items-center gap-3">
            {canCreate && (
              <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm bg-slate-800 text-white" style={{ backgroundColor: pageTheme.accent, color: '#ffffff' }} >
                <FaPlus className="h-4 w-4" />
                Create New
              </button>
            )}
          </div>
        </header>

        {/* Stats Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: pageTheme.muted }}>Total Receipts</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{stats.totalReceipts}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#e6f0ff', color: '#2563eb' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: pageTheme.muted }}>With Inventory Flag</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{stats.withInventoryFlag}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#ecfdf5', color: '#16a34a' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: pageTheme.muted }}>Without Inventory Flag</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{stats.withoutInventoryFlag}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#fff7ed', color: '#b45309' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: pageTheme.muted }}>Created This Month</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{stats.createdThisMonth}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Filter */}
        <section className="p-5 bg-white border mb-6" style={{ borderColor: pageTheme.border }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>Search</label>
              <input type="text" placeholder="Search Receipt ID, Production Order, etc." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200" style={{ borderColor: pageTheme.border }} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200" style={{ borderColor: pageTheme.border }}>
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>Inventory Flag</label>
              <select value={inventoryFilter} onChange={(e) => setInventoryFilter(e.target.value)} className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200" style={{ borderColor: pageTheme.border }}>
                <option value="All">All</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <button onClick={() => { setCurrentPage(1); fetchReceipts(); }} className="px-5 py-2 font-medium transition-all duration-200 shadow-sm bg-slate-800 text-white" style={{ backgroundColor: pageTheme.accent, color: '#ffffff' }}>Search</button>
          </div>
        </section>

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: pageTheme.border }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: pageTheme.border }}>
            <h2 className="text-xl font-semibold" style={{ color: pageTheme.text }}>Receipt from Production</h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center" style={{ color: pageTheme.muted }}>Loading receipts...</div>
          ) : currentItems.length === 0 ? (
            <div className="p-8 text-center" style={{ color: pageTheme.muted }}>No receipts found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs font-semibold uppercase" style={{ backgroundColor: '#f3f4f6', color: pageTheme.muted }}>
                  <tr>
                    {[{ key: 'ReceiptID', label: 'Receipt ID' },{ key: 'ReceiptDate', label: 'Date' },{ key: 'ProductionOrderID', label: 'Production Order' },{ key: 'ProductionOrderQty', label: 'Production Qty' },{ key: 'ReceivedByUserName', label: 'Received By' },{ key: 'Status', label: 'Status' },{ key: 'InventoryFlag', label: 'Inventory Flag' },{ key: 'actions', label: 'Actions' }].map((col) => (
                      <th key={col.key} className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" onClick={() => col.key !== 'actions' && handleSort(col.key)} style={{ borderBottom: `1px solid ${pageTheme.border}` }}>
                        <div className="flex items-center">
                          {col.label}
                          {col.key !== 'actions' && renderSortArrows(col.key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: pageTheme.border }}>
                  {currentItems.map((receipt) => {
                    const isActive = receipt.Status === 'ACTIVE';
                    const statusText = isActive ? 'Active' : 'Inactive';
                    const hasInventoryFlag = receipt.InventoryFlag === 1 || receipt.InventoryFlag === true;
                    const inventoryFlagText = hasInventoryFlag ? 'Yes' : 'No';
                    return (
                      <tr key={receipt.ReceiptID} className="transition-colors duration-200" style={{ backgroundColor: '#ffffff' }}>
                        <td className="px-6 py-3 font-medium" style={{ color: pageTheme.text }}>{receipt.ReceiptID}</td>
                        <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{new Date(receipt.ReceiptDate).toLocaleDateString()}</td>
                        <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{receipt.ProductionOrderID || '-'}</td>
                        <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{receipt.ProductionOrderQty || '-'}</td>
                        <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{receipt.ReceivedByUserName || '-'}</td>
                        <td className="px-6 py-3">
                          <span className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer rounded-full transition-colors duration-200`} style={{ backgroundColor: isActive ? '#ecfdf5' : '#fff7ed', color: isActive ? '#064e3b' : '#92400e' }}>
                            {statusText}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer rounded-full transition-colors duration-200`} style={{ backgroundColor: hasInventoryFlag ? '#ecfdf5' : '#fff7ed', color: hasInventoryFlag ? '#064e3b' : '#92400e' }}>
                            {inventoryFlagText}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex gap-3 justify-center">
                            {canView && (<button onClick={() => handleView(receipt)} title="View" className="transition-colors duration-200" style={{ color: pageTheme.accent }}><FaEye className="h-4 w-4" /></button>)}
                            {canEdit && (<button onClick={() => handleEdit(receipt)} title="Edit" className="transition-colors duration-200" style={{ color: '#16a34a' }}><FaEdit className="h-4 w-4" /></button>)}
                            {canDelete && (<button onClick={() => handleDelete(receipt.ReceiptID)} title="Delete" className="transition-colors duration-200" style={{ color: '#dc2626' }}><FaTrash className="h-4 w-4" /></button>)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <ReceiptFormModal
  open={showModal}
  onClose={() => setShowModal(false)}
  onSubmit={handleModalSubmit}
  initialData={selectedReceipt}
  isEdit={!!selectedReceipt}
  currentUser={currentUser}
/>
        {/* Receipt View Modal */}
        <ReceiptViewModal
  open={viewModalOpen}
  onClose={() => setViewModalOpen(false)}
  receiptId={selectedReceipt}
/>
      </main>
    </div>
  );
};

export default ReceiptFromProductionItems;
